import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Mail, Eye, TrendingUp, AlertCircle, Send, Clock, Users, Loader2, CheckCircle2, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { formatDate } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';

const ESTADO_COLOR: Record<string, string> = {
  borrador:   'bg-gray-100 text-gray-600',
  programada: 'bg-blue-100 text-blue-700',
  enviando:   'bg-yellow-100 text-yellow-700',
  enviada:    'bg-green-100 text-green-700',
  cancelada:  'bg-red-100 text-red-600',
};
const ESTADO_LABEL: Record<string, string> = {
  borrador: 'Borrador', programada: 'Programada', enviando: 'Enviando...', enviada: 'Enviada', cancelada: 'Cancelada',
};

const ENVIO_ESTADO_ICON: Record<string, React.ReactNode> = {
  pendiente: <Clock size={14} className="text-gray-400" />,
  enviado:   <CheckCircle2 size={14} className="text-green-500" />,
  fallido:   <XCircle size={14} className="text-red-500" />,
};

function StatCard({ icon: Icon, label, value, sub, color }: {
  icon: React.ElementType; label: string; value: string | number; sub?: string; color: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-text-secondary">{label}</span>
        <div className={`p-2 rounded-lg ${color}`}><Icon size={16} /></div>
      </div>
      <p className="text-2xl font-bold text-text-primary">{value}</p>
      {sub && <p className="text-xs text-text-secondary mt-0.5">{sub}</p>}
    </div>
  );
}

export function CampanaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: campana, isLoading } = useQuery({
    queryKey: ['campana', id],
    queryFn: () => api.get(`/api/marketing/campanas/${id}`).then(r => r.data),
    refetchInterval: (data: any) => data?.estado === 'enviando' ? 3000 : false,
  });

  const { data: envios = [], isLoading: loadingEnvios } = useQuery({
    queryKey: ['campana-envios', id],
    queryFn: () => api.get(`/api/marketing/campanas/${id}/envios`).then(r => r.data),
    enabled: !!campana && campana.estado !== 'borrador',
    refetchInterval: campana?.estado === 'enviando' ? 3000 : false,
  });

  const enviarMutation = useMutation({
    mutationFn: () => api.post(`/api/marketing/campanas/${id}/enviar`).then(r => r.data),
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['campana', id] });
      qc.invalidateQueries({ queryKey: ['campana-envios', id] });
      qc.invalidateQueries({ queryKey: ['campanas'] });
      toast.success(`Enviando a ${data.destinatarios} destinatarios`);
    },
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error al enviar')),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-text-secondary" />
      </div>
    );
  }

  if (!campana) return null;

  const tasaApertura = campana.totalEnviados > 0
    ? Math.round((campana.totalAbiertos / campana.totalEnviados) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate('/marketing')} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary mt-0.5">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold text-text-primary">{campana.nombre}</h2>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ESTADO_COLOR[campana.estado] || 'bg-gray-100'}`}>
              {ESTADO_LABEL[campana.estado] || campana.estado}
            </span>
          </div>
          <div className="flex items-center gap-4 mt-1 flex-wrap">
            {campana.plantilla && (
              <span className="text-sm text-text-secondary">Plantilla: <span className="text-text-primary">{campana.plantilla.nombre}</span></span>
            )}
            {campana.fechaEnvio && (
              <span className="text-sm text-text-secondary">Enviada: {formatDate(campana.fechaEnvio)}</span>
            )}
            {campana.fechaProgramada && campana.estado === 'programada' && (
              <span className="text-sm text-text-secondary flex items-center gap-1">
                <Clock size={13} /> Programada: {formatDate(campana.fechaProgramada)}
              </span>
            )}
          </div>
        </div>
        {(campana.estado === 'borrador' || campana.estado === 'programada') && (
          <button
            onClick={() => enviarMutation.mutate()}
            disabled={enviarMutation.isPending}
            className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors flex-shrink-0"
          >
            {enviarMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Enviar ahora
          </button>
        )}
      </div>

      {/* Stats */}
      {campana.estado !== 'borrador' && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users}      label="Destinatarios" value={campana.totalDestinatarios} color="bg-blue-50 text-blue-600" />
          <StatCard icon={Mail}       label="Enviados"      value={campana.totalEnviados}      color="bg-green-50 text-green-600" />
          <StatCard icon={Eye}        label="Abiertos"      value={campana.totalAbiertos}      color="bg-purple-50 text-purple-600" />
          <StatCard icon={TrendingUp} label="Tasa apertura" value={`${tasaApertura}%`}         sub={campana.totalFallidos > 0 ? `${campana.totalFallidos} fallidos` : undefined} color="bg-orange-50 text-orange-600" />
        </div>
      )}

      {campana.estado === 'enviando' && (
        <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 text-yellow-800 text-sm">
          <Loader2 size={16} className="animate-spin flex-shrink-0" />
          Enviando correos... Esta página se actualiza automáticamente.
        </div>
      )}

      {/* Barra de progreso (si está enviando) */}
      {campana.estado === 'enviando' && campana.totalDestinatarios > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex justify-between text-xs text-text-secondary mb-2">
            <span>Progreso de envío</span>
            <span>{campana.totalEnviados + campana.totalFallidos} / {campana.totalDestinatarios}</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${Math.round(((campana.totalEnviados + campana.totalFallidos) / campana.totalDestinatarios) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Tabla de envíos */}
      {envios.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="px-5 py-4 border-b border-gray-50">
            <h3 className="font-semibold text-text-primary">Detalle de envíos</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-text-secondary">Paciente</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-text-secondary">Email</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-text-secondary">Estado</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-text-secondary">Apertura</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-text-secondary">Fecha envío</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {envios.map((e: any) => (
                  <tr key={e.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-text-primary">{e.nombrePaciente}</td>
                    <td className="px-4 py-3 text-text-secondary">{e.email}</td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1.5">
                        {ENVIO_ESTADO_ICON[e.estado]}
                        <span className={e.estado === 'fallido' ? 'text-red-600' : e.estado === 'enviado' ? 'text-green-700' : 'text-text-secondary'}>
                          {e.estado === 'enviado' ? 'Enviado' : e.estado === 'fallido' ? 'Fallido' : 'Pendiente'}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {e.abierto ? (
                        <span className="flex items-center gap-1 text-purple-600">
                          <Eye size={13} /> {e.fechaApertura ? formatDate(e.fechaApertura) : 'Sí'}
                        </span>
                      ) : (
                        <span className="text-text-secondary">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      {e.fechaEnvio ? formatDate(e.fechaEnvio) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {campana.estado === 'borrador' && (
        <div className="bg-gray-50 rounded-xl border border-gray-100 p-8 text-center">
          <Mail size={36} className="mx-auto text-gray-200 mb-3" />
          <p className="font-medium text-text-primary">Esta campaña está en borrador</p>
          <p className="text-sm text-text-secondary mt-1">Envíala para ver las estadísticas de envío</p>
        </div>
      )}
    </div>
  );
}
